import React, { useEffect, useRef, useState } from 'react';
import { Monitor, Shield, Moon, Sun, Users, FileText, Lock } from 'lucide-react';
import { useDarkMode } from '../hooks/useDarkMode';
import { useSystemStatus } from '../hooks/useSystemStatus';
import { useNotifications } from '../hooks/useNotifications';
import { SystemStatusModal } from './SystemStatusModal';
import UserProfile from './UserProfile';

interface LayoutProps {
  children: React.ReactNode;
  activeTab: string;
  onTabChange: (tab: string) => void;
  adminStatus: 'LOADING' | 'NOT_FOUND' | 'PENDING' | 'APPROVED' | 'DENIED';
  onNavigateToProfile?: () => void;
  showLockRequests?: boolean;
}

export function Layout({ children, activeTab, onTabChange, adminStatus, onNavigateToProfile, showLockRequests = false }: LayoutProps) {
  const { isDark, toggleDarkMode } = useDarkMode();
  const { status } = useSystemStatus();
  const { unreadCount } = useNotifications();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [activePillStyle, setActivePillStyle] = useState({ left: 0, width: 0, opacity: 0 });

  const baseTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Monitor },
    { id: 'devices', label: 'Devices', icon: Shield },
  ];

  // Add admin management and audit tabs only for approved admins
  // Add lock requests tab only if feature flag is enabled
  const adminTabs = [];
  if (adminStatus === 'APPROVED') {
    adminTabs.push({ id: 'user-management', label: 'Users', icon: Users });
    
    // Only add lock requests if feature flag is enabled
    if (showLockRequests) {
      adminTabs.push({ id: 'lock-requests', label: 'Lock Requests', icon: Lock });
    }
    
    adminTabs.push({ id: 'audit', label: 'Audit Logs', icon: FileText });
  }

  const tabs = [...baseTabs, ...adminTabs];

  useEffect(() => {
    const updateActivePill = () => {
      const container = tabsContainerRef.current;
      if (!container) {
        return;
      }

      const activeButton = container.querySelector<HTMLButtonElement>(`button[data-tab-id="${activeTab}"]`);
      if (!activeButton) {
        setActivePillStyle(prev => ({ ...prev, opacity: 0 }));
        return;
      }

      setActivePillStyle({
        left: activeButton.offsetLeft + -3.5, // Adjust for 1px border
        width: activeButton.offsetWidth - 2,
        opacity: 1,
      });
    };

    updateActivePill();
    window.addEventListener('resize', updateActivePill);
    return () => window.removeEventListener('resize', updateActivePill);
  }, [activeTab, tabs.length]);

  const overallStatus = status.database === 'online' && status.api === 'online' ? 'online' : 
                      status.database === 'checking' || status.api === 'checking' ? 'checking' : 'offline';

  const getStatusColor = () => {
    switch (overallStatus) {
      case 'online':
        return 'from-emerald-400 via-emerald-500 to-teal-600';
      case 'offline':
        return 'from-red-400 via-red-500 to-red-600';
      case 'checking':
        return 'from-blue-400 via-blue-500 to-indigo-600';
    }
  };

  const getStatusGlow = () => {
    switch (overallStatus) {
      case 'online':
        return 'shadow-emerald-500/30';
      case 'offline':
        return 'shadow-red-500/30';
      case 'checking':
        return 'shadow-blue-500/30';
    }
  };

  return (
    <div className={`min-h-screen transition-all duration-500 ${
      isDark 
        ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950' 
        : 'bg-gradient-to-br from-slate-50 via-blue-50/50 to-indigo-100/50'
    }`}>
      {/* Header */}
      <header className={`backdrop-blur-xl border-b sticky top-0 z-40 transition-all duration-300 ${
        isDark 
          ? 'bg-slate-900/90 border-slate-700/50 shadow-xl shadow-slate-900/20' 
          : 'bg-white/90 border-slate-200/50 shadow-xl shadow-slate-900/5'
      }`}>
        <div className="w-full px-6 lg:px-8">
          <div className="flex items-center gap-4 min-h-[84px] py-3">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 transition-transform hover:scale-105">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className={`text-xl font-bold transition-all duration-300 ${
                  isDark 
                    ? 'bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent' 
                    : 'bg-gradient-to-r from-slate-900 via-blue-900 to-purple-900 bg-clip-text text-transparent'
                }`}>
                  LockWise
                </h1>
                <p className={`text-sm transition-all duration-300 ${
                  isDark ? 'text-slate-400' : 'text-slate-600'
                }`}>
                  Device Management System
                </p>
              </div>
            </div>

            <div className="flex-1 flex justify-center min-w-0">
              <div
                ref={tabsContainerRef}
                className={`relative inline-flex items-center gap-1 rounded-full px-1.5 py-1.5 border transition-all duration-300 max-w-full overflow-x-auto ${
                  isDark
                    ? 'bg-slate-800/50 border-slate-700/60 shadow-lg shadow-slate-950/30'
                    : 'bg-white/40 border-slate-300/60 shadow-lg shadow-slate-900/5'
                }`}>
                <div
                  aria-hidden="true"
                  className={`absolute top-1.5 bottom-1.5 rounded-full transition-all duration-300 ease-out pointer-events-none ${
                    isDark
                      ? 'bg-slate-700/70 shadow-md shadow-slate-950/50'
                      : 'bg-white shadow-sm shadow-slate-200/80'
                  }`}
                  style={{
                    width: `${activePillStyle.width}px`,
                    transform: `translateX(${activePillStyle.left}px)`,
                    opacity: activePillStyle.opacity,
                  }}
                />
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      data-tab-id={tab.id}
                      onClick={() => onTabChange(tab.id)}
                      className={`relative z-10 flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer ${
                        isActive
                          ? isDark
                            ? 'text-white'
                            : 'text-slate-900'
                          : isDark
                            ? 'text-slate-400'
                            : 'text-slate-600'
                      }`}
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${isActive ? 'scale-110' : 'scale-100'}`} />
                      <span className="font-medium">{tab.label}</span>
                      {((tab.id === 'user-management') || (tab.id === 'lock-requests' && showLockRequests)) && unreadCount > 0 && (
                        <span className={`flex-shrink-0 w-4 h-4 text-xs font-bold rounded-full flex items-center justify-center ${
                          isDark
                            ? 'bg-red-500/80 text-white'
                            : 'bg-red-500 text-white'
                        }`}>
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              {/* User Profile */}
              <UserProfile onNavigateToProfile={onNavigateToProfile ?? (() => {})} />

              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className={`p-2.5 rounded-xl transition-all duration-300 ${
                  isDark 
                    ? 'bg-slate-800/50 hover:bg-slate-700/50 text-amber-400 hover:text-amber-300' 
                    : 'bg-slate-100/50 hover:bg-slate-200/50 text-slate-600 hover:text-slate-700'
                } backdrop-blur-sm border border-slate-200/20 dark:border-slate-700/20 hover:scale-105 shadow-lg`}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? (
                  <Sun className="w-4 h-4" />
                ) : (
                  <Moon className="w-4 h-4" />
                )}
              </button>

              {/* System Status - Fixed width to prevent jumping */}
              <button
                onClick={() => setShowStatusModal(true)}
                className="flex items-center space-x-3 group relative min-w-[140px] hover:scale-105 transition-transform"
                title="Click to view detailed system status"
              >
                <div className={`w-8 h-8 bg-gradient-to-br ${getStatusColor()} rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-105 shadow-lg ${getStatusGlow()}`}>
                  <div className={`w-2 h-2 bg-white rounded-full ${
                    overallStatus === 'online' ? 'animate-pulse' : 
                    overallStatus === 'checking' ? 'animate-bounce' : ''
                  }`}></div>
                </div>
                <div className="text-left">
                  <div className={`text-sm font-semibold transition-all duration-300 ${
                    isDark ? 'text-slate-200' : 'text-slate-700'
                  }`}>
                    {overallStatus === 'checking' ? 'Checking...' : `System ${overallStatus}`}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

      </header>

      {/* Main Content - This is the key fix */}
      <main className={`min-h-[calc(100vh-140px)] transition-all duration-500 ${
        isDark 
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950' 
          : 'bg-gradient-to-br from-slate-50 via-blue-50/50 to-indigo-100/50'
      }`}>
        <div className="px-6 lg:px-8 py-8">
          {children}
        </div>
      </main>

      {/* System Status Modal */}
      <SystemStatusModal 
        isOpen={showStatusModal} 
        onClose={() => setShowStatusModal(false)} 
      />
    </div>
  );
}
